import Assistant from '@curity/oauth-assistant';
import axios from 'axios';
import { compose, legacy_createStore as createStore, combineReducers, applyMiddleware, Reducer } from 'redux';
import { composeWithDevTools } from '@redux-devtools/extension';
import { persistReducer, persistStore } from 'redux-persist';
import promise from 'redux-promise-middleware';
import storageSession from 'redux-persist/lib/storage/session'
import thunk from 'redux-thunk';

// REDUCERS
import {
    tokenAssistantReducer,
    verifyEmailReducer,
    getConsentReducer,
    getPaymentReducer,
    ApiSandboxReducers,
    ApiProductionReducers,
    contentfulReducer,
    goLiveReducer,
    getUserReducer,
    createUserReducer
} from './reducers';

// STATES

import { AssistantState, IVerifyEmailState, IUserState, ICreateUserState } from './reducers/userManagementReducer/userManagementReducer';

import { ApiSandboxState } from './reducers/ApiManagementSandboxReducers/ApiManagementSandboxReducers';

import { createApplicationReducer, CreateApplicationState } from './reducers/CreateApplicationReducer/CreateApplicationReducer';


import { createAssistant } from '../OAuthAssistant/settings';
import { resolveEnvironment } from '../EnvironmentResolver';
import { ConsentState, PaymentState } from './reducers/ScaReducer/ScaReducer';
import { dataManagementReducers, DataManagementState } from './reducers/dataManagementReducers';
import { IContentfulProductsState } from './reducers/ContentfulReducers/ContentfulReducers';
import { IThunkArguments } from './reduxTypes';
import { ApiProductionState } from './reducers/ApiManagementProductionReducer/ApiManagementProductionReducers';
import { IGoLiveState } from './reducers/GoLiveReducer/GoLiveReducer';
import storage from 'redux-persist/lib/storage';
import { certificateReducer, ICertificateState } from './reducers/CertificateManagementReducers';


const {
    BASE_URL: apiUrl,
    REDIRECT_URL: redirectUrl,
    SILENT_REDIRECT_URL: silentRedirectUrl,
} = resolveEnvironment();

const {
    REACT_APP_API_VER: apiVer = '/v1',
} = process.env


// localhost:8080 is an external server which hosts the html file for passing on authentication
const assistant: Assistant = createAssistant({
    redirect_uri: redirectUrl,
});

// We use a different assistant for silent authentication as it requires a different redirect URL
const silentAssistant: Assistant = createAssistant({
    redirect_uri: silentRedirectUrl
})

const sandboxAxiosInstance = axios.create({
    baseURL: apiUrl + apiVer,
    timeout: 25000
});

const productionAxiosInstance = axios.create({
    baseURL: apiUrl + apiVer,
    headers: {
        Environment: 'production'
    },
    timeout: 25000
})

export const APPLICATION_URL = apiUrl + apiVer;

const extraArgument: IThunkArguments = {
    sandboxAxiosInstance,
    productionAxiosInstance,
    assistant,
    silentAssistant,
}

/**
 * Thunk middleware allows us to dispatch a function, rather than static data
 * Promise-middleware allows us to create promise-based dispatches, giving us 
 * knowledge of whether networking calls are pending, successful or failed
 */
const middlewares: any = [thunk.withExtraArgument(extraArgument), promise];

export interface WebPortalState {
    tokenAssistant: AssistantState,
    visitationHandler: AssistantState
    consentState: ConsentState,
    paymentState: PaymentState
    createApplication: CreateApplicationState
    dataManagement: DataManagementState
    sandboxAPI: ApiSandboxState,
    productionAPI: ApiProductionState
    verifyState: IVerifyEmailState,
    contentfulState: IContentfulProductsState,
    goLiveState: IGoLiveState,
    developerState: IUserState,
    createDeveloperState: ICreateUserState
    certificateState: ICertificateState
}

const Development = true; // Note: consider using process.env to derive this

const persistAssistantConfig = {
    key: 'authentication',
    storage: storageSession,
    blacklist: ['token', 'rejected']
}

const persistFirstTimeConfig = {
    key: 'oldVisitor',
    storage,
    whitelist: ['hasLoggedIn']
}


const pTokenReducer = persistReducer(persistAssistantConfig, tokenAssistantReducer)
const ppTokenReducer = persistReducer(persistFirstTimeConfig, tokenAssistantReducer)

const combinedReducers: Reducer<WebPortalState> = combineReducers({
    tokenAssistant: pTokenReducer, //Assistant
    dataManagement: dataManagementReducers,
    sandboxAPI: ApiSandboxReducers, // Sandbox
    productionAPI: ApiProductionReducers,
    createApplication: createApplicationReducer, //Create application
    paymentState: getPaymentReducer,
    consentState: getConsentReducer,
    verifyState: verifyEmailReducer,
    contentfulState: contentfulReducer,
    goLiveState: goLiveReducer,
    visitationHandler: ppTokenReducer,
    developerState: getUserReducer,
    createDeveloperState: createUserReducer,
    certificateState: certificateReducer
})

const composer = Development ? composeWithDevTools(compose(applyMiddleware(...middlewares))) : compose(applyMiddleware(...middlewares));

export const store = createStore(combinedReducers, composer as any);
export const persistor = persistStore(store);
